'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import { ArrowTopRightOnSquareIcon, BellIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { BellIcon as BellSolidIcon } from '@heroicons/react/24/solid';
import { useAuth } from './AuthProvider';
import { shellNotificationsApi as notificationsApi } from '@/app/services/shellApi';
import type { NotificationData } from '@/app/types/interfaces';
import { describeNotification, formatNotificationAge } from '@/app/lib/notificationDisplay';
import { CourseLink } from './CoursePeek/CourseLink';

function ageAgo(dateString: string, now: number) {
  const age = formatNotificationAge(dateString, now);
  return age === 'just now' || /\d$/.test(age) ? age : `${age} ago`;
}

interface NotificationCenterProps { className?: string }

export default function NotificationCenter({ className = '' }: NotificationCenterProps) {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();

  const fetchNotifications = async () => {
    if (!user?.id) { setNotifications([]); return; }
    try {
      setLoading(true);
      setNotifications(await notificationsApi.getAll(user.id));
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    void fetchNotifications();
    const interval = window.setInterval(() => void fetchNotifications(), 30_000);
    return () => window.clearInterval(interval);
  // Notification data deliberately refreshes when the signed-in identity changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', closeOnOutsidePointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsidePointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      await notificationsApi.markAsRead(notificationId);
      setNotifications((items) => items.map((item) => item.id === notificationId ? { ...item, is_read: true } : item));
    } catch (error) { console.error('Error marking notification as read:', error); }
  };
  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;
    try {
      await notificationsApi.markAllAsRead(user.id);
      setNotifications((items) => items.map((item) => ({ ...item, is_read: true })));
    } catch (error) { console.error('Error marking all notifications as read:', error); }
  };
  const handleDeleteNotification = async (notificationId: number) => {
    try {
      await notificationsApi.delete(notificationId);
      setNotifications((items) => items.filter((item) => item.id !== notificationId));
    } catch (error) { console.error('Error deleting notification:', error); }
  };

  const unreadCount = notifications.filter((notification) => !notification.is_read).length;
  const unreadLabel = unreadCount ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}` : 'Notifications';
  const now = Date.now();
  const described = notifications.map(notification => ({ notification, view: describeNotification(notification, now) }));
  const actNow = described.filter(entry => entry.view.actNow);
  const fyi = described.filter(entry => !entry.view.actNow);

  return (
    <div className={`relative shrink-0 ${className}`} ref={dropdownRef}>
      <button ref={triggerRef} type="button" onClick={() => setIsOpen((open) => !open)} className="relative flex h-12 w-12 items-center justify-center rounded-card text-slate-600 transition hover:bg-slate-100 hover:text-slate-950 focus:outline-none focus:ring-2 focus:ring-red-500 dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white md:h-11 md:w-11" aria-label={unreadLabel} aria-expanded={isOpen} aria-controls={panelId} aria-haspopup="dialog">
        {unreadCount ? <BellSolidIcon className="h-5 w-5" /> : <BellIcon className="h-5 w-5" />}
        {unreadCount > 0 && <span className="absolute right-0 top-0 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-700 px-1 text-[10px] font-bold text-white">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      {isOpen && (
        <section id={panelId} role="dialog" aria-label="Notifications" onKeyDown={event => { if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); setIsOpen(false); triggerRef.current?.focus(); } }} className="fixed inset-x-3 top-[calc(env(safe-area-inset-top)+4.5rem)] z-[70] flex max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-9rem)] flex-col overflow-hidden rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface)] text-[var(--ss-text)] shadow-e3 sm:absolute sm:inset-x-auto sm:right-0 sm:top-[calc(100%+0.75rem)] sm:max-h-[min(30rem,calc(100dvh-7rem))] sm:w-96">
          <header className="flex min-h-14 items-center gap-2 border-b border-[var(--ss-border)] py-1 pl-4 pr-1">
            <h2 className="text-sm font-semibold">Notifications</h2>
            {actNow.length ? <span className="text-xs text-[var(--ss-text-muted)]">{actNow.length} need{actNow.length === 1 ? 's' : ''} you</span> : null}
            <span className="flex-1" />
            {unreadCount > 0 && <button type="button" onClick={() => void handleMarkAllAsRead()} className="min-h-11 rounded-control px-3 text-xs font-semibold text-[var(--ss-focus)] hover:bg-[var(--ss-surface-subtle)]">Mark all read</button>}
            <button type="button" onClick={() => { setIsOpen(false); triggerRef.current?.focus(); }} className="flex h-11 w-11 items-center justify-center rounded-control text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)]" aria-label="Close notifications"><XMarkIcon className="h-5 w-5" /></button>
          </header>
          <div className="min-h-0 flex-1 overflow-y-auto" aria-live="polite" aria-busy={loading}>
            {loading && notifications.length === 0 ? <p className="p-5 text-center text-sm text-[var(--ss-text-muted)]">Loading notifications…</p> : notifications.length === 0 ? (
              <div className="px-5 py-8 text-center"><p className="text-sm font-semibold">You’re all caught up</p><p className="mt-1 text-xs text-[var(--ss-text-muted)]">Seat alerts and account updates show up here.</p></div>
            ) : <>
              {actNow.length ? <section aria-labelledby={`${panelId}-act`} className="space-y-2 p-3">
                <h3 id={`${panelId}-act`} className="px-1 text-xs font-semibold text-[var(--ss-text-muted)]">Act now</h3>
                {actNow.map(({ notification, view }) => (
                  <article key={notification.id} className="rounded-card border border-[var(--ss-positive)] p-3" data-notification-urgent>
                    <p className="flex items-center justify-between gap-2 text-xs font-semibold text-[var(--ss-positive)]"><span>Seat open</span><span className="font-medium">open {formatNotificationAge(notification.created_at, now)}</span></p>
                    <p className="mt-1 text-sm font-semibold leading-5">{view.courseId ? <CourseLink courseId={view.courseId} className="hover:underline" onOpen={() => setIsOpen(false)}>{view.title}</CourseLink> : view.title}</p>
                    {view.details.length ? <p className="mt-0.5 text-xs text-[var(--ss-text-muted)]">{view.details.join(' · ')}</p> : null}
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      {view.registerUrl ? <a href={view.registerUrl} target="_blank" rel="noreferrer" onClick={() => void handleMarkAsRead(notification.id)} className="inline-flex min-h-9 items-center gap-1 rounded-control bg-[var(--ss-positive)] px-3 text-xs font-semibold text-[var(--ss-canvas)] hover:brightness-110">Register on WebReg<ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" aria-hidden="true" /></a> : null}
                      <button type="button" onClick={() => void handleMarkAsRead(notification.id)} className="inline-flex min-h-9 items-center rounded-control border border-[var(--ss-border)] px-3 text-xs font-semibold hover:bg-[var(--ss-surface-subtle)]">Done</button>
                    </div>
                  </article>
                ))}
              </section> : null}
              {fyi.length ? <section aria-labelledby={`${panelId}-fyi`} className={actNow.length ? 'border-t border-[var(--ss-border)] pt-2' : 'pt-2'}>
                <h3 id={`${panelId}-fyi`} className="px-4 pb-1 pt-1 text-xs font-semibold text-[var(--ss-text-muted)]">For your information</h3>
                <ul>
                  {fyi.map(({ notification, view }) => (
                    <li key={notification.id} className="group flex items-start gap-2.5 py-2 pl-4 pr-1">
                      <span aria-hidden="true" className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${notification.is_read ? 'bg-transparent' : 'bg-[var(--ss-focus)]'}`} />
                      <div className="min-w-0 flex-1">
                        <p className={`break-words text-sm leading-5 ${notification.is_read ? 'text-[var(--ss-text-muted)]' : ''}`}>
                          {view.kind === 'seat-open' ? <>Seat opened in {view.courseId ? <CourseLink courseId={view.courseId} className="font-medium hover:underline" onOpen={() => setIsOpen(false)}>{view.title}</CourseLink> : view.title}</> : notification.url ? <a href={notification.url} onClick={() => void handleMarkAsRead(notification.id)} className="hover:underline">{view.title}</a> : view.title}
                        </p>
                        <p className="mt-0.5 text-xs text-[var(--ss-text-muted)]">{[...view.details.slice(0, 2), ageAgo(notification.created_at, now)].join(' · ')}</p>
                      </div>
                      {!notification.is_read ? <button type="button" onClick={() => void handleMarkAsRead(notification.id)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)] hover:text-[var(--ss-text)]" aria-label="Mark notification as read"><CheckIcon className="h-4 w-4" /></button> : null}
                      <button type="button" onClick={() => void handleDeleteNotification(notification.id)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-control text-[var(--ss-text-muted)] hover:bg-[var(--ss-surface-subtle)] hover:text-[var(--ss-text)]" aria-label="Delete notification"><XMarkIcon className="h-4 w-4" /></button>
                    </li>
                  ))}
                </ul>
              </section> : null}
            </>}
          </div>
        </section>
      )}
    </div>
  );
}
